/**
 * Minimal Navigation API polyfill for test environments
 * Based on: https://developer.mozilla.org/en-US/docs/Web/API/Navigation_API
 *
 * Traversal fidelity rules (mirrors the real API):
 * - back/forward/traverseTo reuse the EXISTING entry: same key, same id, same
 *   state, and emit navigationType 'traverse'
 * - push mints a new key and truncates forward entries
 * - replace keeps the entry's key and mints a new id; it does NOT truncate
 * - `index` is maintained on every live entry; disposed entries report -1
 */

type NavigationHistoryEntry = {
  id: string
  key: string
  url: string
  index: number
  sameDocument: boolean
  getState(): unknown
}

type NavigationDestination = {
  id: string
  url: string
  key: string
  index: number
  sameDocument: boolean
  getState(): unknown
}

type NavigationType = 'reload' | 'push' | 'replace' | 'traverse'

type NavigationInterceptHandler = {
  handler: () => Promise<void> | void
  focusReset?: 'after-transition' | 'manual'
  scroll?: 'after-transition' | 'manual'
}

class NavigateEventImpl extends Event {
  canIntercept: boolean
  destination: NavigationDestination
  downloadRequest: string | null
  formData: FormData | null
  hashChange: boolean
  info: unknown
  navigationType: NavigationType
  signal: AbortSignal
  userInitiated: boolean
  private interceptHandler:
    | ((options: NavigationInterceptHandler) => void)
    | null = null

  constructor(
    destination: NavigationDestination,
    navigationType: NavigationType,
    info: unknown,
    onIntercept: (handler: () => Promise<void> | void) => void,
    signal: AbortSignal,
  ) {
    super('navigate', { bubbles: false, cancelable: false })
    this.canIntercept = true
    this.destination = destination
    this.downloadRequest = null
    this.formData = null
    this.hashChange = false
    this.info = info
    this.navigationType = navigationType
    // Aborted when a later navigation supersedes this one. An intercept handler
    // that keeps writing after this fires is writing on behalf of a navigation
    // the user already left.
    this.signal = signal
    this.userInitiated = false
    this.interceptHandler = (opts: NavigationInterceptHandler) => {
      onIntercept(opts.handler)
    }
  }

  intercept(options: NavigationInterceptHandler): void {
    if (this.interceptHandler) {
      this.interceptHandler(options)
    }
  }
}

type NavigationOptions = {
  state?: unknown
  history?: 'auto' | 'push' | 'replace'
  info?: unknown
}

type NavigationResult = {
  committed: Promise<void>
  finished: Promise<void>
}

const toAbsoluteUrl = (url: string): string =>
  url.startsWith('http')
    ? url
    : `http://localhost${url.startsWith('/') ? '' : '/'}${url}`

/**
 * Entry state lives in a holder so `updateCurrentEntry` can patch an entry in
 * place without touching its `key` or `id` — the whole point of the in-place
 * write is that the entry's identity survives it.
 */
const stateHolders = new WeakMap<NavigationHistoryEntry, { value: unknown }>()

class CurrentEntryChangeEventImpl extends Event {
  from: NavigationHistoryEntry
  /** null for an in-place `updateCurrentEntry`; the type for a real navigation. */
  navigationType: NavigationType | null

  constructor(
    from: NavigationHistoryEntry,
    navigationType: NavigationType | null,
  ) {
    super('currententrychange', { bubbles: false, cancelable: false })
    this.from = from
    this.navigationType = navigationType
  }
}

const rejected = (message: string): NavigationResult => {
  const failure = Promise.reject(new Error(message))
  // Both handles share one rejection; callers may await either or neither.
  failure.catch(() => {})
  return { committed: failure, finished: failure }
}

export class NavigationPolyfill extends EventTarget {
  private entryList: NavigationHistoryEntry[] = []
  private currentIndex = 0
  private keyCounter = 0
  private idCounter = 0
  /** The navigation whose intercept handler is still running, if any. */
  private inFlight: AbortController | null = null

  constructor(initialUrl = '/') {
    super()

    // Create initial entry
    this.entryList.push(this.createEntry(initialUrl, {}))
    this.reindex()
  }

  private createEntry(
    url: string,
    state: unknown,
    key = `key-${this.keyCounter++}`,
  ): NavigationHistoryEntry {
    const holder = { value: state }
    const entry: NavigationHistoryEntry = {
      id: `id-${this.idCounter++}`,
      key,
      url: toAbsoluteUrl(url),
      index: -1,
      sameDocument: true,
      getState: () => holder.value,
    }
    stateHolders.set(entry, holder)
    return entry
  }

  /** Keeps `index` truthful after any mutation of the entry list. */
  private reindex(): void {
    for (const [index, entry] of this.entryList.entries()) {
      entry.index = index
    }
  }

  /**
   * Destination of a push/replace. Verified in Chrome 2026-08-15: a
   * not-yet-committed destination exposes no identity — `key` and `id` are the
   * empty string and `index` is -1. Only `navigation.currentEntry` after commit
   * carries the key.
   */
  private pendingDestination(
    entry: NavigationHistoryEntry,
  ): NavigationDestination {
    return {
      id: '',
      key: '',
      url: entry.url,
      index: -1,
      sameDocument: entry.sameDocument,
      getState: () => entry.getState(),
    }
  }

  /** Destination of a traverse: the target entry's own identity and state. */
  private traverseDestination(
    entry: NavigationHistoryEntry,
    index: number,
  ): NavigationDestination {
    return {
      id: entry.id,
      key: entry.key,
      url: entry.url,
      index,
      sameDocument: entry.sameDocument,
      getState: () => entry.getState(),
    }
  }

  /**
   * Dispatches the navigate event, commits, then runs the intercept handler.
   *
   * Commit-before-handler matches Chrome (verified 2026-08-15): a `navigate`
   * listener still sees the OLD `currentEntry`, but inside the intercept
   * handler `currentEntry` is already the destination. It also makes nested
   * navigations ordered correctly — a redirect issued from inside a handler
   * commits after the navigation that provoked it, not before it.
   */
  private dispatch(
    destination: NavigationDestination,
    navigationType: NavigationType,
    info: unknown,
    apply: () => void,
  ): NavigationResult {
    let interceptHandler: (() => Promise<void> | void) | null = null

    // Starting a navigation supersedes any in-flight one, exactly as Chrome
    // does: the older event's signal aborts and its `finished` rejects. This is
    // not an edge case — a redirect issued from inside an intercept handler
    // supersedes the navigation that provoked it.
    const controller = new AbortController()
    const superseded = this.inFlight
    this.inFlight = controller
    superseded?.abort()

    const event = new NavigateEventImpl(
      destination,
      navigationType,
      info,
      (handler) => {
        interceptHandler = handler
      },
      controller.signal,
    )

    // Dispatch event synchronously so intercept() is called
    this.dispatchEvent(event)

    const executeNavigation = async () => {
      const from = this.currentEntry
      apply()
      this.dispatchEvent(new CurrentEntryChangeEventImpl(from, navigationType))
      // Run intercept handler if provided
      if (interceptHandler) {
        await interceptHandler()
      }
      if (this.inFlight === controller) this.inFlight = null
      if (controller.signal.aborted) {
        throw new Error('Navigation was aborted')
      }
    }

    const finished = executeNavigation()
    // Mark handled so a superseded navigation nobody awaited is not an
    // unhandled rejection; awaiting callers still observe the rejection.
    finished.catch(() => {})

    return { committed: Promise.resolve(), finished }
  }

  private traverse(targetIndex: number, info: unknown): NavigationResult {
    const target = this.entryList[targetIndex]
    // Same entry object => same key, same id, same state.
    return this.dispatch(
      this.traverseDestination(target, targetIndex),
      'traverse',
      info,
      () => {
        this.currentIndex = targetIndex
      },
    )
  }

  get currentEntry(): NavigationHistoryEntry {
    return this.entryList[this.currentIndex]
  }

  entries(): NavigationHistoryEntry[] {
    return [...this.entryList]
  }

  get canGoBack(): boolean {
    return this.currentIndex > 0
  }

  get canGoForward(): boolean {
    return this.currentIndex < this.entryList.length - 1
  }

  navigate(url: string, options: NavigationOptions = {}): NavigationResult {
    const isReplace = options.history === 'replace'
    const replaced = this.entryList[this.currentIndex]
    // replace keeps the key (same slot), mints a new id
    const destinationEntry = this.createEntry(
      url,
      options.state ?? null,
      isReplace ? replaced.key : undefined,
    )
    return this.dispatch(
      this.pendingDestination(destinationEntry),
      isReplace ? 'replace' : 'push',
      options.info,
      () => {
        if (isReplace) {
          replaced.index = -1
          this.entryList[this.currentIndex] = destinationEntry
        } else {
          // push - forward history is disposed
          for (const disposed of this.entryList.slice(this.currentIndex + 1)) {
            disposed.index = -1
          }
          this.entryList.length = this.currentIndex + 1
          this.entryList.push(destinationEntry)
          this.currentIndex = this.entryList.length - 1
        }
        this.reindex()
      },
    )
  }

  reload(options: { state?: unknown; info?: unknown } = {}): NavigationResult {
    return this.navigate(this.currentEntry.url, {
      ...options,
      history: 'replace',
    })
  }

  back(options: { info?: unknown } = {}): NavigationResult {
    if (!this.canGoBack) return rejected('Cannot go back')
    return this.traverse(this.currentIndex - 1, options.info)
  }

  forward(options: { info?: unknown } = {}): NavigationResult {
    if (!this.canGoForward) return rejected('Cannot go forward')
    return this.traverse(this.currentIndex + 1, options.info)
  }

  /**
   * In-place write (§5): patches THIS entry's state snapshot and fires
   * `currententrychange` with `navigationType: null`. It is not a navigation —
   * no `navigate` event, no re-match, no loader re-run, and `key`/`id` survive.
   */
  updateCurrentEntry(options: { state: unknown }): void {
    const entry = this.currentEntry
    const holder = stateHolders.get(entry)
    if (!holder) {
      throw new Error('updateCurrentEntry: no current entry')
    }
    holder.value = options.state
    this.dispatchEvent(new CurrentEntryChangeEventImpl(entry, null))
  }

  traverseTo(key: string, options: { info?: unknown } = {}): NavigationResult {
    const targetIndex = this.entryList.findIndex((e) => e.key === key)
    if (targetIndex === -1) return rejected('Entry not found')
    return this.traverse(targetIndex, options.info)
  }

  // Helper for tests to set initial entries
  setInitialEntries(urls: string[]): void {
    for (const disposed of this.entryList) {
      disposed.index = -1
    }
    this.entryList = urls.map((url) => this.createEntry(url, null))
    this.currentIndex = urls.length - 1
    this.reindex()
  }
}

export type NavigationLike = Pick<
  NavigationPolyfill,
  | 'navigate'
  | 'currentEntry'
  | 'addEventListener'
  | 'removeEventListener'
  | 'entries'
  | 'traverseTo'
  | 'updateCurrentEntry'
  | 'back'
  | 'canGoBack'
>

export function createMemoryNavigation(initialUrl = '/'): NavigationPolyfill {
  return new NavigationPolyfill(initialUrl)
}

// Install polyfill if Navigation API not available
export function installNavigationPolyfill(initialUrl?: string): void {
  Object.defineProperty(globalThis, 'navigation', {
    value: createMemoryNavigation(initialUrl),
    writable: true,
    configurable: true,
  })
}

export function getNavigation(): NavigationPolyfill {
  return (globalThis as typeof globalThis & { navigation: NavigationPolyfill }).navigation
}

// Helper for tests to set initial entries
export function setInitialNavigationEntries(urls: string[]): void {
  const nav = getNavigation()
  if ('setInitialEntries' in nav) {
    nav.setInitialEntries(urls)
  }
}
